import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import TagService from '../../../services/tag-service';
import type { TagData } from '../../../types/tag';

// Get all tags
export const useGetTags = () => {
  return useQuery<TagData[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await TagService.getTags();
      return response as TagData[];
    },
  });
};

// Get single tag by ID
export const useGetTagById = (tagId: string | undefined) => {
  return useQuery<TagData>({
    queryKey: ['tag', tagId],
    queryFn: async () => {
      const response = await TagService.getTagsById(parseInt(tagId!));
      return response as TagData;
    },
    enabled: !!tagId,
  });
};

interface UseCreateOrUpdateTagOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useCreateOrUpdateTag = ({
  onSuccess,
  successMessage,
}: UseCreateOrUpdateTagOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: number;
      data: Partial<TagData>;
    }) => {
      if (id) {
        return TagService.updateTags(id, data);
      }
      return TagService.createTags(data);
    },
    onSuccess: (_, variables) => {
      const message =
        successMessage ??
        (variables.id
          ? 'Tag updated successfully'
          : 'Tag created successfully');
      toast.success(message, { position: 'top-right' });

      queryClient.invalidateQueries({ queryKey: ['tags'] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ['tag', variables.id.toString()],
        });
      }
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [firstKey] = query.queryKey;
          return typeof firstKey === 'string' && firstKey?.startsWith('agent-');
        },
      });
      onSuccess?.();
    },
    onError: () => {
      // const action = id ? "update" : "create";
      toast.error(`Failed to perform operation. Please try again.`);
      // console.error(`Failed to ${action} tag:`, error);
    },
  });
};

interface UseDeleteTagOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useDeleteTag = ({
  onSuccess,
  successMessage,
}: UseDeleteTagOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return TagService.deleteTags(id);
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Tag deleted successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [firstKey] = query.queryKey;
          return typeof firstKey === 'string' && firstKey?.startsWith('agent-');
        },
      });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error('Failed to delete tag. Please try again.');
      console.error('Failed to delete tag:', error);
    },
  });
};

export default useDeleteTag;
